import type { Portfolio } from '@/types'
import { portfolioSyncInterval } from '@pkg/basic'

/** 自動同期 policy を再評価する間隔。 */
export const dbSyncPolicyCheckIntervalMs = 15_000

/** writer lease と lastUsed を更新する heartbeat 間隔。 */
export const lastUsedHeartbeatIntervalMs = portfolioSyncInterval

/** 通常同期に必要な最後の DB write からの静穏時間。 */
export const dbSyncQuietPeriodMs = 5 * 60_000

/** 通常同期に必要な最後の user activity からの idle 時間。 */
export const dbSyncUserIdlePeriodMs = 30_000

/** 最初の未同期 write から snapshot upload を開始するまでの期限。 */
export const dbSyncMaxUnsyncedMs = 60 * 60_000

/** portfolio DB 自動同期 policy の入力。 */
export type PortfolioDbSyncPolicyInput = {
  readonly now: number
  readonly localUpdatedAt: number
  readonly remoteUpdatedAt: number
  readonly dirtySince: number | undefined
  readonly lastUserActivityAt: number
  readonly hasActiveWriteOperation: boolean
  readonly hasProgress: boolean
  readonly isSyncRunning: boolean
  readonly retryNotBefore: number | undefined
}

/** portfolio DB 自動同期を開始する理由。 */
export type PortfolioDbSyncReason = 'quiet' | 'max-unsynced'

/** portfolio DB 自動同期を開始するかを表す判定結果。 */
export type PortfolioDbSyncDecision =
  | {
      readonly shouldSync: true
      readonly reason: PortfolioDbSyncReason
    }
  | {
      readonly shouldSync: false
    }

/** DB backup scheduler が portfolio ごとに保持する retry 状態。 */
export type PortfolioDbSyncSchedulerState = {
  consecutiveFailureCount: number
  retryNotBefore: number | undefined
}

/** DB backup scheduler が全 portfolio で共有する実行状態。 */
export type PortfolioDbSyncExecutionState = {
  isSyncRunning: boolean
}

/** portfolio UUID と対応する DB backup scheduler state。 */
export type PortfolioDbSyncSchedulerScope = {
  readonly portfolioId: string
  readonly state: PortfolioDbSyncSchedulerState
}

/** DB backup scheduler の 1 tick の実行結果。 */
export type PortfolioDbSyncTickResult =
  | { readonly status: 'skipped' }
  | { readonly status: 'succeeded'; readonly reason: PortfolioDbSyncReason }
  | {
      readonly status: 'failed'
      readonly reason: PortfolioDbSyncReason
      readonly error: unknown
    }

/** 失敗回数ごとの固定 retry delay。 */
const dbSyncRetryDelayMs = [60_000, 5 * 60_000, 15 * 60_000] as const

/**
 * writable と plan から自動 DB backup の対象かを判定する。
 * @param params writable 状態と portfolio plan を受け取る。
 * @returns paid plan の writable portfolio なら true を返す。
 */
export const canSchedulePortfolioDbBackup = ({
  isWritable,
  plan,
}: {
  readonly isWritable: boolean
  readonly plan: Portfolio['plan']
}): boolean => isWritable && plan !== 'free' && plan !== 'mini'

/**
 * 初期状態の DB backup scheduler state を生成する。
 * @returns 実行も retry もない scheduler state を返す。
 */
export const createPortfolioDbSyncSchedulerState =
  (): PortfolioDbSyncSchedulerState => ({
    consecutiveFailureCount: 0,
    retryNotBefore: undefined,
  })

/**
 * 全 portfolio で共有する初期 DB backup 実行状態を生成する。
 * @returns backup が実行されていない状態を返す。
 */
export const createPortfolioDbSyncExecutionState =
  (): PortfolioDbSyncExecutionState => ({
    isSyncRunning: false,
  })

/**
 * 現在の portfolio UUID に対応する scheduler scope を返す。
 * UUID が変わった場合は新しい state object を生成し、旧 portfolio の実行中処理と分離する。
 * @param params 現在の scope と評価対象 portfolio UUID を受け取る。
 * @returns 同じ UUID なら既存 scope、異なる UUID なら初期状態の新しい scope を返す。
 */
export const getPortfolioDbSyncSchedulerScope = ({
  scope,
  portfolioId,
}: {
  readonly scope: PortfolioDbSyncSchedulerScope | undefined
  readonly portfolioId: string
}): PortfolioDbSyncSchedulerScope => {
  if (scope?.portfolioId === portfolioId) {
    return scope
  }
  return {
    portfolioId,
    state: createPortfolioDbSyncSchedulerState(),
  }
}

/**
 * 連続失敗回数に対応する retry delay を返す。
 * @param params 1 以上の連続失敗回数を受け取る。
 * @returns 1 回目は 60 秒、2 回目は 5 分、3 回目以降は 15 分を返す。
 */
export const getPortfolioDbSyncRetryDelayMs = ({
  consecutiveFailureCount,
}: {
  readonly consecutiveFailureCount: number
}): number => {
  const index = Math.min(
    Math.max(consecutiveFailureCount, 1),
    dbSyncRetryDelayMs.length,
  )
  return dbSyncRetryDelayMs[index - 1] ?? dbSyncRetryDelayMs[2]
}

/**
 * 現在の時刻と同期状態から portfolio DB 自動同期の開始可否を判定する。
 * @param input timestamp、blocker、retry 状態を受け取る。
 * @returns 同期開始可否と、開始する場合はその理由を返す。
 */
export const decidePortfolioDbSync = (
  input: PortfolioDbSyncPolicyInput,
): PortfolioDbSyncDecision => {
  const {
    now,
    localUpdatedAt,
    remoteUpdatedAt,
    dirtySince,
    lastUserActivityAt,
    hasActiveWriteOperation,
    hasProgress,
    isSyncRunning,
    retryNotBefore,
  } = input

  if (localUpdatedAt <= remoteUpdatedAt) {
    return { shouldSync: false }
  }

  if (isSyncRunning || hasActiveWriteOperation || hasProgress) {
    return { shouldSync: false }
  }

  if (retryNotBefore !== undefined && now < retryNotBefore) {
    return { shouldSync: false }
  }

  if (dirtySince !== undefined && now - dirtySince >= dbSyncMaxUnsyncedMs) {
    return { shouldSync: true, reason: 'max-unsynced' }
  }

  const isDatabaseQuiet = now - localUpdatedAt >= dbSyncQuietPeriodMs
  const isUserIdle = now - lastUserActivityAt >= dbSyncUserIdlePeriodMs
  if (isDatabaseQuiet && isUserIdle) {
    return { shouldSync: true, reason: 'quiet' }
  }

  return { shouldSync: false }
}

/**
 * policy を評価し、開始対象の場合だけ DB backup と retry state 遷移を実行する。
 * @param params scheduler state、policy 入力、backup callback を受け取る。
 * @returns skip、成功、失敗のいずれかを返し、backup の失敗は throw しない。
 */
export const runPortfolioDbSyncSchedulerTick = async ({
  state,
  executionState,
  policyInput,
  startSync,
}: {
  readonly state: PortfolioDbSyncSchedulerState
  readonly executionState: PortfolioDbSyncExecutionState
  readonly policyInput: Omit<
    PortfolioDbSyncPolicyInput,
    'isSyncRunning' | 'retryNotBefore'
  >
  readonly startSync: (params: {
    readonly reason: PortfolioDbSyncReason
  }) => Promise<void>
}): Promise<PortfolioDbSyncTickResult> => {
  const decision = decidePortfolioDbSync({
    ...policyInput,
    isSyncRunning: executionState.isSyncRunning,
    retryNotBefore: state.retryNotBefore,
  })
  if (!decision.shouldSync) {
    return { status: 'skipped' }
  }

  executionState.isSyncRunning = true
  try {
    await startSync({ reason: decision.reason })
    state.consecutiveFailureCount = 0
    state.retryNotBefore = undefined
    return { status: 'succeeded', reason: decision.reason }
  } catch (error) {
    state.consecutiveFailureCount += 1
    state.retryNotBefore =
      policyInput.now +
      getPortfolioDbSyncRetryDelayMs({
        consecutiveFailureCount: state.consecutiveFailureCount,
      })
    return { status: 'failed', reason: decision.reason, error }
  } finally {
    executionState.isSyncRunning = false
  }
}
